import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { api, ApiError } from "./lib/api";
import { queryKeys } from "./lib/queryKeys";
import { Login } from "./pages/Login";
import { Shell } from "./components/Shell";
import { PlatformShell } from "./components/PlatformShell";
import { Automations } from "./pages/Automations";
import { TeamAudit } from "./components/reports/TeamAudit";
import { Dashboard } from "./pages/Dashboard";
import { HelpdeskHome } from "./pages/HelpdeskHome";
import { Calls } from "./pages/Calls";
import { CannedResponses } from "./pages/CannedResponses";
import { FailedReplies } from "./pages/FailedReplies";
import { Settings } from "./pages/Settings";
import { Notifications } from "./pages/Notifications";
import { TicketsView } from "./pages/TicketsView";
import { CrmShell } from "./pages/crm/CrmShell";
import { AmDashboard } from "./pages/crm/AmDashboard";
import { CompaniesList } from "./pages/crm/CompaniesList";
import { CompanyPage } from "./pages/crm/CompanyPage";
import { Duplicates } from "./pages/crm/Duplicates";
import { CallOverOrigin, SalesShell } from "./pages/sales/SalesShell";
import { backgroundOf } from "./lib/call-overlay";
import { SalesBoard } from "./pages/sales/SalesBoard";
import { CallsList } from "./pages/sales/CallsList";
import { SalesReport } from "./pages/sales/SalesReport";
import { MeetingsShell } from "./pages/meetings/MeetingsShell";
import { MeetingsHome } from "./pages/meetings/MeetingsHome";
import { FieldIndex, FieldShell } from "./pages/field/FieldShell";
import { FieldHome } from "./pages/field/FieldHome";
import { FieldTasks } from "./pages/field/FieldTasks";
import { QuickNotes } from "./pages/field/QuickNotes";

export function App() {
  /*
   * Read before the early returns below, which would otherwise change how many
   * hooks run. A call opened from a sales page carries that page here, and the
   * routes render against it so it stays mounted under the dialog -- see
   * `call-overlay.ts`.
   */
  const location = useLocation();
  const background = backgroundOf(location);

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: api.me,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted">
        Loading…
      </div>
    );
  }

  // 401 is the normal unauthenticated path, not an error worth surfacing.
  if (me.isError) {
    const unauthenticated =
      me.error instanceof ApiError && me.error.status === 401;
    return unauthenticated ? (
      <Login />
    ) : (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-red-600">
        Could not reach the Pulse API. {String(me.error)}
      </div>
    );
  }

  const user = me.data!;

  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<Shell user={user} />}>
          {/* All open work is the sensible landing view for somebody who has
              not chosen: it is the only one that is never empty for a new
              agent. Anybody can choose another in their own settings, and
              HelpdeskHome is what reads that choice. */}
          <Route index element={<HelpdeskHome />} />
          <Route path="v/:viewId" element={<TicketsView user={user} />} />
          {/* The selected ticket lives in the URL so links stay pasteable. */}
          <Route path="v/:viewId/:ticketId" element={<TicketsView user={user} />} />
          <Route path="dashboard" element={<Dashboard user={user} />} />
          {/* The phone system's call history. The open call is in the path, as a
              ticket is, so a link to one lands on it with the list behind. */}
          <Route path="calls" element={<Calls user={user} />} />
          <Route path="calls/:callId" element={<Calls user={user} />} />
          <Route path="canned" element={<CannedResponses />} />
          {/* Every agent's, filtered server-side to the channels they work --
              the banner links here. */}
          <Route path="failed" element={<FailedReplies />} />
          {/* Routing rules decide who work lands on, so the page is a
              manager's. Redirected rather than shown-and-refused: the API
              already returns 403, and a page that only renders an error is
              worse than never offering the link. */}
          <Route
            path="automations"
            element={
              user.role === "admin" ? (
                <Automations user={user} />
              ) : (
                <Navigate to="/v/open" replace />
              )
            }
          />
          {/* Same reasoning as automations: the API is admin-only, so a
              non-admin is redirected rather than shown a page that can only
              render an error. */}
          <Route
            path="audit"
            element={
              user.role === "admin" ? (
                <TeamAudit />
              ) : (
                <Navigate to="/v/open" replace />
              )
            }
          />
          {/*
            The old queue and ticket URLs. Kept as redirects rather than deleted:
            agents paste ticket links to each other, and those links live on in
            Gmail threads and chat history long after this refactor.
          */}
          <Route path="q/:slug" element={<Navigate to="/v/open" replace />} />
          <Route path="q/:slug/:ticketId" element={<LegacyTicketRedirect />} />
          <Route path="tickets/:ticketId" element={<LegacyTicketRedirect />} />
          <Route path="list" element={<Navigate to="/v/open" replace />} />
        </Route>

        {/*
          Configuration, in a frame of its own.

          It lived under the Helpdesk's shell and so wore the Helpdesk's sidebar,
          which put a list of ticket queues beside a page about channels, teams
          and CRM fields. Settings belong to the platform -- one users table, one
          role column, one page -- and the rail is what the platform looks like.

          Still a manager's, and still redirected rather than shown-and-refused,
          matching Automations.
        */}
        <Route element={<PlatformShell user={user} title="Settings" />}>
          <Route
            path="/settings"
            element={
              user.role === "admin" ? (
                <Settings user={user} />
              ) : (
                <Navigate to="/v/open" replace />
              )
            }
          />
        </Route>

        {/*
          The notification history, in the same platform frame and for a related
          reason: it deliberately spans all four products, so framing it in any
          one product's navigation would be picking a side.

          Everybody's, unlike settings -- these are your own notifications, and
          the API takes no user id from the client at all.
        */}
        <Route element={<PlatformShell user={user} title="Notifications" />}>
          <Route path="/notifications" element={<Notifications />} />
        </Route>

        {/*
          The CRM. Its own shell rather than a section of the Helpdesk's: the two are
          separate products behind one app rail, and the sidebar belongs to the
          product. They share the frame, the session and the socket.
        */}
        <Route path="/crm" element={<CrmShell user={user} />}>
          <Route index element={<Navigate to="/crm/companies" replace />} />
          {/* The account manager's overview. Above companies in the sidebar for
              the same reason the Helpdesk and Sales Calls put theirs there: it
              answers "where does my book stand" before you pick a list. */}
          <Route path="dashboard" element={<AmDashboard user={user} />} />
          <Route path="companies" element={<CompaniesList />} />
          {/* A company is the unit of everything, so it is the only deep link
              the CRM needs -- contacts and activity live inside it. */}
          <Route path="companies/:companyId" element={<CompanyPage user={user} />} />
          <Route path="duplicates" element={<Duplicates user={user} />} />
          {/* CRM settings are now a tab on the platform's one settings page --
              a role is not per product, and two settings pages implied it was.
              The old URL still resolves, for anybody who has it bookmarked. */}
          <Route path="settings" element={<Navigate to="/settings" replace />} />
          <Route path="*" element={<Navigate to="/crm/companies" replace />} />
        </Route>

        {/*
          Sales calls. Its own shell for the same reason the CRM has one: three
          products behind one rail, and the sidebar belongs to the product rather
          than to the frame. The landing view is the index route because the whole
          module is built around "open it and know what to ring next" -- dropping
          somebody on a list instead would waste the one screen that answers that.
        */}
        <Route path="/sales" element={<SalesShell user={user} />}>
          <Route index element={<SalesBoard user={user} />} />
          <Route path="calls" element={<CallsList user={user} />} />
          {/*
            The detail is a dialog, so this route renders the *list* underneath
            it and the shell overlays the call on top. Deep-linking a call still
            works -- you land on the list with the call open over it, which is
            also where the cross puts you.
          */}
          <Route path="calls/:callId" element={<CallsList user={user} />} />
          {/* Closed calls are the same list with the status pinned, which is why
              they are a route rather than a filter people have to know about. */}
          <Route path="closed" element={<CallsList user={user} closed />} />

          {/* The manager's view: who is loaded, who is late, who is doing what. */}
          <Route path="dashboard" element={<SalesReport user={user} />} />

          <Route path="*" element={<Navigate to="/sales" replace />} />
        </Route>

        {/*
          Meetings. Its own shell beside the other three products rather than a
          section of Sales calls: a visit is worked by the field half of the desk,
          in a van, and somebody opening Pulse to see their week should land on
          their own diary rather than on the call pool.

          Recent, the calendar's views and whose diary are query parameters
          rather than paths, so `meetings/:meetingId` below cannot be reached by
          a URL reading `meetings/recent` -- which would try to open a meeting
          whose id is a word.

          Both routes render the same page, and the meeting opens in a pane
          beside the calendar or feed it was opened from. A deep link lands on
          the calendar with the meeting open, which is also where the cross
          puts you. See `MeetingsHome`.
        */}
        <Route path="/meetings" element={<MeetingsShell user={user} />}>
          <Route index element={<MeetingsHome user={user} />} />
          <Route path=":meetingId" element={<MeetingsHome user={user} />} />
        </Route>

        {/*
          Field Sales: a rep's own front page over the diary and Lane, and a
          private notepad. Its tabs are routes so Home's cards can link
          straight to today's note, and `/field` lands on whichever tab was
          used last. The task list is the detail behind one Home card, so it
          sits under Home rather than being a tab of its own.
        */}
        <Route path="/field" element={<FieldShell user={user} />}>
          <Route index element={<FieldIndex user={user} />} />
          <Route path="home" element={<FieldHome user={user} />} />
          <Route path="tasks" element={<FieldTasks user={user} />} />
          <Route path="notes" element={<QuickNotes user={user} />} />
          <Route path="notes/:day" element={<QuickNotes user={user} />} />
          <Route path="*" element={<Navigate to="/field" replace />} />
        </Route>

        {/* Anything else is a Helpdesk URL, so it lands in the Helpdesk. */}
        <Route element={<Shell user={user} />}>
          <Route path="*" element={<Navigate to="/v/open" replace />} />
        </Route>
      </Routes>

      {/*
        The call itself, against the real URL, when the routes above are drawing
        the page it was opened from. Without a background the sales shell draws
        it instead, over the worklist, as it always has.
      */}
      {background ? (
        <Routes>
          <Route path="/sales/calls/:callId" element={<CallOverOrigin user={user} />} />
        </Routes>
      ) : null}
    </>
  );
}

/**
 * An old ticket link, rehomed into the all-open view.
 *
 * That view is used rather than a guess at the "right" one because a ticket is
 * reachable from any view -- the detail pane loads it by id regardless of
 * whether it appears in that view's list.
 */
function LegacyTicketRedirect() {
  const { ticketId } = useParams();
  return <Navigate to={`/v/open/${ticketId ?? ""}`} replace />;
}
